
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import MultiTranslatedTextField from './MultiTranslatedTextField.vue'
import { useComponentEntries } from '@/composables/use-component-entries'

interface SocialLinkEntryData {
  type: 'social_link_entry'
  platform: string
  url: string
}

interface SocialLinksComponentData {
  name: 'social_links'
  entries: SocialLinkEntryData[]
}

const linkTypeDisplayNames: Record<string, string> = {
  apple_music: 'Apple Music',
  spotify: 'Spotify',
  youtube: 'YouTube',
  instagram: 'Instagram',
  twitter: 'Twitter',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  discord: 'Discord',
  deezer: 'Deezer',
  amazon_music: 'Amazon Music'
}

const linkUrlPlaceholders: Record<string, string> = {
  apple_music: 'https://music.apple.com/artist/...',
  spotify: 'https://open.spotify.com/artist/...',
  youtube: 'https://www.youtube.com/channel/...',
  instagram: 'https://www.instagram.com/...',
  twitter: 'https://twitter.com/...',
  facebook: 'https://www.facebook.com/...',
  tiktok: 'https://www.tiktok.com/@...',
  discord: 'https://discord.gg/...',
  deezer: 'https://www.deezer.com/en/artist/...',
  amazon_music: 'https://music.amazon.com/artists/...'
}

export default defineComponent({
  name: 'SocialLinksComponent',
  components: {
    MultiTranslatedTextField
  },

  props: {
    initialData: {
      type: Object as PropType<{ name: string; entries: any[] } | undefined>,
      default: undefined
    }
  },

  emits: ['change'],

  setup (props, { expose, emit }) {
    const linkTypeOptions = computed(() => {
      return Object.entries(linkTypeDisplayNames).map(([key, value]) => ({
        title: value,
        value: key
      })).sort((a, b) => a.title.localeCompare(b.title))
    })

    const selectedLinkType = ref<string | null>(null)

    const data = ref<SocialLinksComponentData>({
      name: 'social_links',
      entries: []
    })

    watch(() => props.initialData, (newData) => {
      if (newData) {
        data.value = {
          name: 'social_links',
          entries: newData.entries || []
        }
      }
    }, { immediate: true })

    const { removeEntry: removeEntryAndReindex } = useComponentEntries<SocialLinkEntryData>(
      () => data.value.entries,
      {
        translationKeyFields: ['url'],
        computeKeyPrefix: (entries, index) => {
          const entry = entries[index]
          const sameTypeBefore = entries.slice(0, index).filter(e => e.platform === entry.platform).length
          return `social_links.${entry.platform}.[${sameTypeBefore}]`
        }
      }
    )

    const addEntry = () => {
      if (!selectedLinkType.value) return

      const linkType = selectedLinkType.value
      const sameTypeCount = data.value.entries.filter(e => e.platform === linkType).length
      const prefix = `social_links.${linkType}.[${sameTypeCount}]`
      data.value.entries.push({
        type: 'social_link_entry',
        platform: linkType,
        url: prefix + '.url'
      })
      selectedLinkType.value = null
      emit('change')
    }

    const removeEntry = (index: number) => {
      removeEntryAndReindex(index)
      emit('change')
    }

    const getComponentData = () => ({
      name: data.value.name,
      entries: data.value.entries
    })

    expose({ getComponentData })

    return {
      data,
      selectedLinkType,
      linkTypeOptions,
      linkTypeDisplayNames,
      linkUrlPlaceholders,
      addEntry,
      removeEntry
    }
  }
})
